import React, { SVGProps, useEffect, useState } from "react";

export type SvgInHtml = SVGProps<SVGSVGElement>;

export type IconProps = SvgInHtml & {
  dataTestId?: string;
  iconName: string;
  color?: string;
  alt?: string;
  size?: IconSizes | number;
  fill?: "currentColor" | string;
};

const ICON_SIZES = {
  xxxxl: 180,
  xxxl: 96,
  xxl: 64,
  "xl-light": 56,
  xl: 48,
  l: 40,
  m: 32,
  "m-light": 24,
  s: 16,
  xs: 12,
};

const STROKE_WIDTHS: Record<keyof typeof ICON_SIZES, number> = {
  xxxxl: 0.3,
  xxxl: 0.5,
  xxl: 0.5,
  "xl-light": 1,
  xl: 1,
  l: 1.5,
  m: 1.5,
  "m-light": 1.5,
  s: 1.5,
  xs: 1.5,
};

export type IconSizes = keyof typeof ICON_SIZES;

// icons that finished loading, so a component mounting again (e.g. a timeline card scrolling back into view) renders its
// icon on the very first paint instead of an empty placeholder that pops the icon in later and shifts the text beside it
const loadedIcons = new Map<string, React.ElementType>();

export const Icon = ({ dataTestId, iconName, size, className = "", onClick, strokeWidth, fill, color, alt = iconName, ...props }: IconProps) => {
  const [loadedIcon, setLoadedIcon] = useState<{ iconName: string; component: React.ElementType } | null>(null);
  const sizeFromProps = size;
  useEffect(() => {
    if (!iconName || loadedIcons.has(iconName)) return undefined;
    let isUnmounted = false;
    async function getData() {
      const module = await import(`!!@svgr/webpack?-svgo,+titleProp,+ref!../../../../assets/icons/${iconName}.svg`);
      loadedIcons.set(iconName, module.default);
      if (!isUnmounted) setLoadedIcon({ iconName, component: module.default });
    }
    getData();
    return () => {
      isUnmounted = true;
    };
  }, [iconName]);
  const ReactIcon = (iconName ? loadedIcons.get(iconName) : undefined) ?? (loadedIcon?.iconName === iconName ? loadedIcon.component : null);

  const getFill = () => {
    const currentColorIcons = ["klim/map-pin", "klim/logo", "klim/support-chat", "klim/support-chat-filled"];

    if (currentColorIcons.includes(iconName)) {
      return "currentColor";
    }
    if (fill) {
      return fill;
    }
    return "none";
  };

  const sizeAsNumber = typeof sizeFromProps === "number" ? sizeFromProps : sizeFromProps ? ICON_SIZES[sizeFromProps] : 24;
  const getStroke =
    strokeWidth ??
    (typeof sizeFromProps === "number"
      ? sizeAsNumber >= 180
        ? 0.3
        : sizeAsNumber >= 96
        ? 0.5
        : sizeAsNumber >= 64
        ? 0.5
        : sizeAsNumber >= 48
        ? 1
        : 1.5
      : sizeFromProps
      ? STROKE_WIDTHS[sizeFromProps]
      : 1.5);

  return ReactIcon && iconName ? (
    <ReactIcon
      data-testid={dataTestId}
      width={sizeAsNumber}
      height={sizeAsNumber}
      className={`icon icon-${iconName.replace(/[^\w\s]/gi, "-")} ${className}`}
      onClick={onClick}
      strokeWidth={getStroke}
      fill={getFill()}
      color={color || null}
      alt={alt}
      {...props}
    />
  ) : (
    <span className={className} style={{ minWidth: size, minHeight: size }} />
  );
};

export default Icon;
